<template>
  <div
    v-if="show"
    class="subcat-modifier-overlay"
    :class="{ 'subcat-modifier-overlay--kiosk': kiosk }"
    :style="styleObject"
    @click.self="onCancel"
  >
    <div class="subcat-modifier-dialog" role="dialog">
      <div class="subcat-modifier-content">
        <div class="modal-header">
          <div class="d-flex align-items-center modal-title-text">
            <!-- KSK023 — kiosk thumbnail in the header (MOO keeps the title only). -->
            <img
              v-if="kiosk && hasImage"
              :src="item.image"
              alt=""
              class="subcat-thumb"
            />
            <span class="subcat-title">{{ item ? tName(item.item_name) : "" }}</span>
          </div>
          <button
            type="button"
            class="btn-close-x"
            @click="onCancel"
          >
            <span v-if="kiosk" class="material-icons-round">close</span>
            <template v-else>&times;</template>
          </button>
        </div>

        <div class="modal-body">
          <slot></slot>
        </div>

        <div class="modal-footer">
          <button
            class="btn-modal btn-confirm"
            :class="{ disabled: isConfirmDisabled }"
            :disabled="isConfirmDisabled"
            @click="onConfirm"
          >
            {{ $t('itemDetail.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { translatedName } from "@/i18n/menuTranslate";

export default {
  name: "ModalSubCategoryModifier",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // item_detail of the selected sub-category item (used for title + validation)
    item: {
      type: Object,
      default: () => ({}),
    },
    // KSK021 — kiosk theme-2 skin (kiosk ModalItemDetail only; MOO leaves it off).
    kiosk: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    styleObject() {
      // MTH103 — the popup renders inside ModalCustomization's themed root, so
      // --theme-20/30/40 are inherited. Only --color is re-declared here, and it
      // must follow the SAME source as the item-detail sheet
      // (mainThemeColorTheme2), otherwise the Confirm button falls back to the
      // raw merchant colour and clashes with the screen behind it.
      const accent =
        this.$store.state.mainThemeColorTheme2 || this.$store.state.color;
      return {
        "--color": accent,
      };
    },
    hasImage() {
      return !!(
        this.item &&
        this.item.image &&
        !this.item.image.includes("noimage.png")
      );
    },
    // Disable Confirm if any top-level modifier group violates min_qty / max_qty.
    isConfirmDisabled() {
      if (!this.item || !Array.isArray(this.item.modifier_group)) {
        return false;
      }
      return this.item.modifier_group.some((group) => {
        const minQty = parseInt(group.min_qty) || 0;
        const maxQty = parseInt(group.max_qty) || 0;
        const totalSelected = parseInt(group.total_selected) || 0;

        // Compulsory: not enough min
        if (minQty > 0 && totalSelected < minQty) return true;
        // Over max (applies to both Optional and Compulsory)
        if (maxQty > 0 && totalSelected > maxQty) return true;

        return false;
      });
    },
  },
  methods: {
    tName(source) {
      return translatedName(source);
    },

    onConfirm() {
      this.$emit("confirm");
    },
    onCancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped>
.subcat-modifier-overlay {
  position: fixed;
  inset: 0;
  background: rgba(25, 28, 30, 0.45);
  z-index: 10500;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: subcatOverlayFadeIn 0.15s ease-out;
}

.subcat-modifier-dialog {
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  animation: subcatDialogPop 0.18s ease-out;
}

/* MTH103 — theme2 skin: same surface / radius / shadow language as the
   item-detail sheet (ModalCustomization) it opens on top of. */
.subcat-modifier-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--theme-30, #f7f2ed);
  border-radius: 24px;
  border: none;
  box-shadow: 0 20px 40px rgba(25, 28, 30, 0.18);
  overflow: hidden;
  max-height: 90vh;
}

@keyframes subcatOverlayFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes subcatDialogPop {
  from { transform: scale(0.92); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.modal-header {
  border-bottom: none;
  padding: 16px 18px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.modal-title-text {
  flex: 1;
  min-width: 0;
}

.subcat-title {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: #201a1a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mirrors ModalCustomization's .btn-close-modal round glass button. */
.btn-close-x {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
  border: none;
  font-size: 28px;
  font-weight: 400;
  cursor: pointer;
  color: var(--color);
  line-height: 1;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  padding-bottom: 4px;
  transition: transform 0.15s ease;
}

.btn-close-x:active {
  transform: scale(0.9);
}

.modal-body {
  padding: 4px 4px 8px;
  flex: 1 1 auto;
  overflow-y: auto;
  /* Hide the scrollbar like the item-detail sheet (scrolling still works). */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.modal-body::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.modal-footer {
  border-top: none;
  padding: 8px 18px 20px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.btn-modal {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  text-transform: unset;
  cursor: pointer;
  border: 1px solid var(--color);
  font-family: "Inter", sans-serif;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.btn-confirm {
  background-color: var(--color);
  color: #ffffff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.14);
}

.btn-confirm:active {
  transform: scale(0.98);
}

/* Same disabled treatment as ModalCustomization's .btn-add-to-cart.disable */
.btn-confirm.disabled,
.btn-confirm:disabled {
  background: var(--theme-20, #e8e0d8);
  color: #a79e95;
  border-color: transparent;
  box-shadow: none;
  cursor: not-allowed;
  opacity: 1;
}

.btn-confirm.disabled:active,
.btn-confirm:disabled:active {
  transform: none;
}

/* KSK021 — kiosk theme-2 skin, only with the `kiosk` prop (MOO unaffected). */
.subcat-modifier-overlay--kiosk {
  background: var(--ksk-scrim, rgba(20, 26, 20, 0.45));
}
/* KSK023 — P6: 900px popup, thumbnail + 2-line title in the header. */
.subcat-modifier-overlay--kiosk .subcat-modifier-dialog {
  width: calc(100% - 2 * var(--ksk-page-padding, 32px));
  max-width: 900px;
  max-height: calc(var(--ksk-vh, 1vh) * 88);
}
.subcat-modifier-overlay--kiosk .subcat-modifier-content {
  max-height: calc(var(--ksk-vh, 1vh) * 88);
  background: var(--ksk-background);
  border-radius: var(--ksk-radius-popup);
  box-shadow: var(--ksk-shadow-float);
  font-family: var(--ksk-font);
  color: var(--ksk-text);
}
.subcat-modifier-overlay--kiosk .modal-header {
  gap: var(--ksk-space-3);
  padding: var(--ksk-space-4) var(--ksk-space-4) var(--ksk-space-3);
  background: var(--ksk-surface);
  border-bottom: 1px solid var(--ksk-border);
}
.subcat-modifier-overlay--kiosk .subcat-title {
  font-family: var(--ksk-font);
  font-size: var(--ksk-title-size);
  line-height: var(--ksk-title-lh);
  font-weight: 700;
  color: var(--ksk-text);
  white-space: normal;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.subcat-modifier-overlay--kiosk .modal-title-text {
  gap: var(--ksk-space-3);
}
.subcat-modifier-overlay--kiosk .subcat-thumb {
  flex: none;
  width: 112px;
  height: 112px;
  border-radius: 20px;
  object-fit: cover;
  background: var(--ksk-surface-muted);
}
.subcat-modifier-overlay--kiosk .btn-close-x {
  width: 72px;
  height: 72px;
  padding: 0;
  background: var(--ksk-surface-muted);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  box-shadow: none;
  color: var(--ksk-text);
  font-size: 44px;
}
.subcat-modifier-overlay--kiosk .btn-close-x .material-icons-round {
  font-size: 36px;
}
.subcat-modifier-overlay--kiosk .modal-body {
  padding: var(--ksk-space-3);
}
.subcat-modifier-overlay--kiosk .modal-footer {
  padding: var(--ksk-space-3) var(--ksk-space-4) var(--ksk-space-4);
  background: var(--ksk-surface);
  border-top: 1px solid var(--ksk-border);
}
.subcat-modifier-overlay--kiosk .btn-modal {
  height: 96px;
  margin: 0;
  border: 2px solid transparent;
  font-family: var(--ksk-font);
  font-size: var(--ksk-label-size);
}
.subcat-modifier-overlay--kiosk .btn-confirm {
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
  box-shadow: var(--ksk-shadow-float);
}
.subcat-modifier-overlay--kiosk .btn-confirm.disabled,
.subcat-modifier-overlay--kiosk .btn-confirm:disabled {
  background: var(--ksk-surface-muted);
  color: var(--ksk-text-secondary);
  box-shadow: none;
}
</style>
